import dayjs from "dayjs"
import { type TFunction } from "i18next"

import { budgetFormat } from "../../utils/number/budget-format.ts"
import type {
  ComparisonAttributeChange,
  ComparisonDiffEntry,
  ComparisonGroup,
  ComparisonGroupItem,
  ComparisonGroupedValue
} from "./comparison-grouped-value.types.ts"
import type {
  FieldComparison,
  MilestoneDuration,
  MilestoneEntry,
  PaymentEntry,
  PaymentSource,
  ScopeDiff,
  ScopeSource,
  SourceType
} from "./types.ts"

interface GroupedCellCopy {
  identicalMessage: string
  itemNounSingular: string
  itemNounPlural: string
  /** What a group *is* for this field — "project type" for Scope. Payment
   * Terms groups nothing real, so it names its one synthetic group. */
  groupNounSingular: string
  groupNounPlural: string
}

interface GroupedComparisonMapping {
  groupedValues: Partial<Record<SourceType, ComparisonGroupedValue>>
  groupedCellCopy: GroupedCellCopy
}

/** Matches `groupMilestones`' own fallback key, so a milestone with no
 * project type lands in the same bucket in the changes list and in the
 * full listing instead of appearing twice under two different keys. */
const UNGROUPED_ID = "unknown"

function otherGroupName(t: TFunction): string {
  return t("documentSync.groupedComparison.otherGroup")
}

/** i18n key per duration unit, so "2 weeks" renders as weeks rather than
 * being silently relabelled as days. An unknown unit reads as days — the
 * backend's own fallback. */
const DURATION_KEY_BY_UNIT: Record<string, string> = {
  day: "documentSync.groupedComparison.durationDays",
  days: "documentSync.groupedComparison.durationDays",
  week: "documentSync.groupedComparison.durationWeeks",
  weeks: "documentSync.groupedComparison.durationWeeks",
  month: "documentSync.groupedComparison.durationMonths",
  months: "documentSync.groupedComparison.durationMonths",
  year: "documentSync.groupedComparison.durationYears",
  years: "documentSync.groupedComparison.durationYears"
}

function formatDuration(duration: MilestoneDuration, t: TFunction): string {
  const key =
    DURATION_KEY_BY_UNIT[duration.unit?.trim().toLowerCase() ?? "days"] ??
    DURATION_KEY_BY_UNIT.days
  return t(key, { count: duration.value })
}

/** The backend normalizes every milestone date to `YYYY-MM-DD` before
 * serializing, so this reads the calendar day as written — never through
 * the viewer's timezone, which would drag an all-day date back a day. */
function formatMilestoneDate(value: string | null | undefined): string | null {
  if (!value) return null
  const parsed = dayjs(value.slice(0, 10))
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : null
}

function formatDateRange(
  start: string | null | undefined,
  end: string | null | undefined
): string | null {
  const from = formatMilestoneDate(start)
  const to = formatMilestoneDate(end)
  if (from && to) return from === to ? from : `${from} – ${to}`
  return from ?? to
}

function milestoneDetail(
  {
    amount,
    duration,
    start_date: startDate,
    end_date: endDate
  }: {
    amount?: string
    duration?: MilestoneDuration | null
    start_date?: string | null
    end_date?: string | null
  },
  t: TFunction
): string {
  const parts: string[] = []
  if (duration) parts.push(formatDuration(duration, t))
  if (amount !== undefined) parts.push(budgetFormat(amount))
  const dates = formatDateRange(startDate, endDate)
  if (dates) parts.push(dates)
  return parts.join(" · ")
}

function milestoneToItem(
  milestone: MilestoneEntry,
  t: TFunction
): ComparisonGroupItem {
  return {
    id: milestone.id,
    label: milestone.name,
    detail: milestoneDetail(milestone, t)
  }
}

/**
 * Mirrors `ScopeOfWorkBlockList`'s own grouping key/fallback
 * (`project_type_id || project_type || "unknown"`, name
 * `project_type_name || "Other"`) so the comparison cell's groups read the
 * same as the document editor's — never re-derived or flattened.
 */
function groupMilestones(source: ScopeSource, t: TFunction): ComparisonGroup[] {
  const order: string[] = []
  const groups = new Map<string, ComparisonGroup>()

  source.milestones.forEach((milestone) => {
    const groupId = milestone.project_type_id ?? UNGROUPED_ID
    const groupName = milestone.project_type_name ?? otherGroupName(t)

    if (!groups.has(groupId)) {
      groups.set(groupId, { id: groupId, name: groupName, items: [] })
      order.push(groupId)
    }
    groups.get(groupId)!.items.push(milestoneToItem(milestone, t))
  })

  return order.map((id) => groups.get(id)!)
}

/**
 * `scope_diff`'s `changed` entries only carry the fields that actually
 * changed — an unchanged amount/duration is simply absent, not repeated.
 * `afterSource` (the structured milestones for whichever column the diff is
 * attached to) fills those gaps so `before`/`after` can each render as a
 * complete item, per `ComparisonDiffEntry`'s own contract.
 */
function buildScopeDiffEntries(
  diff: ScopeDiff | null | undefined,
  afterSource: ScopeSource | undefined,
  t: TFunction
): ComparisonDiffEntry[] | undefined {
  if (!diff) return undefined

  const afterById = new Map(
    (afterSource?.milestones ?? []).map((milestone) => [
      milestone.id,
      milestone
    ])
  )

  const added: ComparisonDiffEntry[] = diff.added.map((milestone) => ({
    id: milestone.id,
    status: "added",
    groupId: milestone.project_type_id ?? UNGROUPED_ID,
    groupName: milestone.project_type_name ?? otherGroupName(t),
    after: milestoneToItem(milestone, t)
  }))

  const removed: ComparisonDiffEntry[] = diff.removed.map((milestone) => ({
    id: milestone.id,
    status: "removed",
    groupId: milestone.project_type_id ?? UNGROUPED_ID,
    groupName: milestone.project_type_name ?? otherGroupName(t),
    before: milestoneToItem(milestone, t)
  }))

  const changed: ComparisonDiffEntry[] = diff.changed.map((entry) => {
    const after = afterById.get(entry.id)
    // Only the fields that actually changed are present on a changed
    // entry, so each side falls back to the after-source milestone's own
    // value — `undefined` means "unchanged", and `null` is a real value a
    // duration/date can take.
    const pick = <T>(changedValue: T | undefined, current: T): T =>
      changedValue !== undefined ? changedValue : current

    const previousAmount = entry.old_amount ?? after?.amount
    const currentAmount = entry.new_amount ?? after?.amount
    const previousDuration = pick(entry.old_duration, after?.duration ?? null)
    const currentDuration = pick(entry.new_duration, after?.duration ?? null)
    const previousStart = pick(entry.old_start_date, after?.start_date ?? null)
    const currentStart = pick(entry.new_start_date, after?.start_date ?? null)
    const previousEnd = pick(entry.old_end_date, after?.end_date ?? null)
    const currentEnd = pick(entry.new_end_date, after?.end_date ?? null)
    // `name` is confirmed live to be absent entirely on a changed entry
    // whose name itself didn't change (a duration-only change arrives as
    // just `{id, old_duration, new_duration}`) — fall back to the
    // after-source milestone's real name rather than rendering a blank
    // label.
    const fallbackName = entry.name ?? after?.name ?? ""
    const previousName = entry.old_name ?? fallbackName
    const currentName = entry.new_name ?? fallbackName
    const notSet = t("documentSync.groupedComparison.notSet")
    const changes: ComparisonAttributeChange[] = []
    const record = (key: string, before: string, after: string) =>
      changes.push({
        attribute: t(`documentSync.groupedComparison.attribute.${key}`),
        before,
        after
      })

    if (entry.old_name !== undefined || entry.new_name !== undefined) {
      record("name", previousName || notSet, currentName || notSet)
    }
    if (entry.old_amount !== undefined || entry.new_amount !== undefined) {
      record(
        "price",
        previousAmount !== undefined ? budgetFormat(previousAmount) : notSet,
        currentAmount !== undefined ? budgetFormat(currentAmount) : notSet
      )
    }
    if (entry.old_duration !== undefined || entry.new_duration !== undefined) {
      record(
        "duration",
        previousDuration ? formatDuration(previousDuration, t) : notSet,
        currentDuration ? formatDuration(currentDuration, t) : notSet
      )
    }
    if (
      entry.old_start_date !== undefined ||
      entry.new_start_date !== undefined
    ) {
      record(
        "startDate",
        formatMilestoneDate(previousStart) ?? notSet,
        formatMilestoneDate(currentStart) ?? notSet
      )
    }
    if (entry.old_end_date !== undefined || entry.new_end_date !== undefined) {
      record(
        "endDate",
        formatMilestoneDate(previousEnd) ?? notSet,
        formatMilestoneDate(currentEnd) ?? notSet
      )
    }
    if (
      entry.old_project_type_name !== undefined ||
      entry.new_project_type_name !== undefined
    ) {
      record(
        "projectType",
        entry.old_project_type_name ?? otherGroupName(t),
        entry.new_project_type_name ?? otherGroupName(t)
      )
    }

    return {
      id: entry.id,
      status: "changed",
      groupId:
        entry.new_project_type_id ?? after?.project_type_id ?? UNGROUPED_ID,
      groupName:
        entry.new_project_type_name ??
        after?.project_type_name ??
        otherGroupName(t),
      before: {
        id: entry.id,
        label: previousName,
        detail: milestoneDetail(
          {
            amount: previousAmount,
            duration: previousDuration,
            start_date: previousStart,
            end_date: previousEnd
          },
          t
        )
      },
      after: {
        id: entry.id,
        label: currentName,
        detail: milestoneDetail(
          {
            amount: currentAmount,
            duration: currentDuration,
            start_date: currentStart,
            end_date: currentEnd
          },
          t
        )
      },
      changes
    }
  })

  return [...added, ...removed, ...changed]
}

/**
 * `scope_structured` is real for every source that has a document (`lead`
 * included — a Lead's `project_phases` are structured too, unlike payment
 * terms); a `null` per-source is a genuine "nothing to structure" (no
 * linked Proposal), handled by the caller simply omitting that column from
 * `groupedValues` so `ComparisonTable` falls back to its plain
 * `values`/`FieldValueLines` rendering for that one cell.
 *
 * `diffColumnKey` names which single column the `scope_diff` belongs to.
 * Confirmed live (PR #1255, a real Contract sync-status call): `scope_diff`
 * is always Lead-relative — comparing the row's own document against the
 * Lead's milestones — regardless of which column that endpoint uses as its
 * plain-text comparison baseline (a Contract with a linked Proposal still
 * diffed against the Lead, not the Proposal, in the confirmed response).
 * `diffColumnKey` is therefore always "whichever column is this document
 * being generated" rather than "whichever column is the baseline" — the two
 * Generate modals know this unambiguously (their own document side, never
 * the baseline side). The 3-column Project-creation modal has no single
 * "document being generated" column, so it instead passes whichever of
 * Proposal/Contract the backend already recommends (`recommended_source`),
 * or omits it entirely when the recommendation is Lead itself — an
 * approximation, since the true diff is always against Lead regardless of
 * recommendation, kept only to avoid showing the affordance on a column
 * that isn't the one thing scope_diff is actually relative to.
 */
function mapScope(
  comparison: FieldComparison,
  columnKeys: SourceType[],
  t: TFunction,
  diffColumnKey: SourceType | undefined
): GroupedComparisonMapping | undefined {
  const structured = comparison.scope_structured
  if (!structured) return undefined

  const groupedValues: Partial<Record<SourceType, ComparisonGroupedValue>> = {}
  const diffEntries =
    diffColumnKey && structured[diffColumnKey]
      ? buildScopeDiffEntries(
          comparison.scope_diff,
          structured[diffColumnKey]!,
          t
        )
      : undefined

  columnKeys.forEach((key) => {
    const source = structured[key]
    if (!source) return

    groupedValues[key] = {
      groups: groupMilestones(source, t),
      diff: key === diffColumnKey ? diffEntries : undefined
    }
  })

  if (Object.keys(groupedValues).length === 0) return undefined

  return {
    groupedValues,
    groupedCellCopy: {
      identicalMessage: t(
        "documentSync.groupedComparison.scopeIdenticalMessage"
      ),
      itemNounSingular: t(
        "documentSync.groupedComparison.milestoneNounSingular"
      ),
      itemNounPlural: t("documentSync.groupedComparison.milestoneNounPlural"),
      groupNounSingular: t(
        "documentSync.groupedComparison.projectTypeNounSingular"
      ),
      groupNounPlural: t("documentSync.groupedComparison.projectTypeNounPlural")
    }
  }
}

function formatPaymentDetail(payment: PaymentEntry, t: TFunction): string {
  const parts: string[] = []
  const percentage = parseFloat(payment.percentage)
  if (!Number.isNaN(percentage)) parts.push(`${percentage}%`)
  parts.push(budgetFormat(payment.amount))
  if (payment.due_date) {
    const parsed = new Date(payment.due_date)
    if (!Number.isNaN(parsed.getTime())) {
      parts.push(
        t("documentSync.groupedComparison.dueDate", {
          date: parsed.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric"
          })
        })
      )
    }
  }
  return parts.join(" · ")
}

function paymentToItem(
  payment: PaymentEntry,
  t: TFunction
): ComparisonGroupItem {
  return {
    id: payment.id,
    label: payment.name ?? t("documentSync.groupedComparison.unnamedPayment"),
    detail: formatPaymentDetail(payment, t)
  }
}

/** Payment Terms has no Project-Type concept of its own (`project_types` is
 * always `[]`) — one synthetic group, per `ComparisonGroup`'s own doc
 * comment on callers with nothing to group by. */
function groupPayments(source: PaymentSource, t: TFunction): ComparisonGroup[] {
  return [
    {
      id: "payment-schedule",
      name: t("documentSync.groupedComparison.paymentScheduleGroupName"),
      items: source.payments.map((payment) => paymentToItem(payment, t))
    }
  ]
}

/**
 * `payment_terms_structured.lead` is always `null` (`core.Project` has no
 * structured payment model) — that column is simply omitted from
 * `groupedValues`, so the Lead cell keeps rendering its existing plain-text
 * `lead_display` summary rather than a broken or empty grouped box.
 * `payment_terms_diff` is always `null` today, so no column here ever gets
 * a `diff` — `GroupedComparisonCell` renders no "View changes" affordance
 * as a result, by design (see `FieldComparison.payment_terms_diff`).
 */
function mapPaymentTerms(
  comparison: FieldComparison,
  columnKeys: SourceType[],
  t: TFunction
): GroupedComparisonMapping | undefined {
  const structured = comparison.payment_terms_structured
  if (!structured) return undefined

  const groupedValues: Partial<Record<SourceType, ComparisonGroupedValue>> = {}

  columnKeys.forEach((key) => {
    const source = structured[key]
    if (!source) return

    groupedValues[key] = { groups: groupPayments(source, t) }
  })

  if (Object.keys(groupedValues).length === 0) return undefined

  return {
    groupedValues,
    groupedCellCopy: {
      identicalMessage: t(
        "documentSync.groupedComparison.paymentTermsIdenticalMessage"
      ),
      itemNounSingular: t("documentSync.groupedComparison.paymentNounSingular"),
      itemNounPlural: t("documentSync.groupedComparison.paymentNounPlural"),
      groupNounSingular: t(
        "documentSync.groupedComparison.paymentGroupNounSingular"
      ),
      groupNounPlural: t(
        "documentSync.groupedComparison.paymentGroupNounPlural"
      )
    }
  }
}

/**
 * Adapts a raw `FieldComparison` row's new structured backend fields
 * (`scope_structured`/`scope_diff`, `payment_terms_structured`) into the
 * `groupedValues`/`groupedCellCopy` shape `ComparisonTable` renders via
 * `GroupedComparisonCell` — the one place all three Generate/Confirm-time
 * modals build this mapping, rather than three parallel implementations.
 *
 * Returns `undefined` for any field key other than `scope`/`payment_terms`,
 * and also when the row's structured field is itself `null` (an older
 * cached response that predates the backend's structured response, or a
 * field the backend genuinely has nothing structured for) — the caller
 * keeps building its row's plain `values` exactly as before in that case,
 * so this is purely additive.
 */
function mapFieldComparisonToGroupedValues(
  comparison: FieldComparison,
  columnKeys: SourceType[],
  t: TFunction,
  diffColumnKey?: SourceType
): GroupedComparisonMapping | undefined {
  if (comparison.key === "scope") {
    return mapScope(comparison, columnKeys, t, diffColumnKey)
  }
  if (comparison.key === "payment_terms") {
    return mapPaymentTerms(comparison, columnKeys, t)
  }
  return undefined
}

export { type GroupedComparisonMapping, mapFieldComparisonToGroupedValues }
